import { inject } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
} from "@angular/router";
import { ScreenType } from "backend/model/screenType";
import {
  APTContext,
  ISimplifiedStructureNode,
} from "src/app/services/aptContext/apt-context";
import { DocumentNavigationService } from "src/app/services/document-navigation.service";
import { APTCodes } from "src/app/workspace/aptCodes";
import {
  CONFIRM_RESPONSE,
  type StandardConfirmationDialogRequest,
} from "src/standard/standard-confirmation-dialog/standard-confirmation-dialog.types";
import { ModalDialogService } from "src/standard/standard-modal-button/showModalDialog";

export function getConflictResolverGuard(
  screenType: ScreenType,
): CanActivateFn {
  return (async (next: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const router = inject(Router);
    const modalDialogService = inject(ModalDialogService);
    const aptContext = inject(APTContext);
    const documentNavigationService = inject(DocumentNavigationService);

    let node: ISimplifiedStructureNode | undefined;

    switch (screenType) {
      case ScreenType.FsaReviewPlanScreen: {
        const parent: ActivatedRouteSnapshot | null = next;
        let nodeId: string | undefined = undefined;
        nodeId = getNodeIdFromPath(parent);
        node = await aptContext.findDMNodeById(nodeId ?? "");
        break;
      }
      case ScreenType.DesignFsaAudit:
      case ScreenType.ExecuteFsaAudit:
      case ScreenType.DesignAndExecuteFsaAudit: {
        const parent: ActivatedRouteSnapshot | null = next;
        const nodeId: string | undefined =
          parent.paramMap.get("screenId") ?? undefined;
        node = await aptContext.findDMNodeById(nodeId ?? "");
        break;
      }
      default: {
        node = await aptContext.findScreenNodeByType(screenType);

        if (node === undefined) {
          await documentNavigationService.getTableofContentAsync(
            aptContext.engagementId(),
            true,
          );
          await aptContext.waitForDocumentStructureFetched();
          node = await aptContext.findScreenNodeByType(screenType);
        }
      }
    }
    if (aptContext.shouldShowConflictWarning()) {
      if ((node?.conflicts?.length ?? 0) > 0) {
        const SAVING_CLOSE_CONFIG = {
          title: $localize`Warning`,
          message: APTCodes.getMessage(
            "WarningMessageWhileOpeningConflictedDocument",
          ),
          confirmText: $localize`Resolve`,
          declineText: $localize`Open`,
          cancelText: $localize`Cancel`,
        } satisfies StandardConfirmationDialogRequest;

        const dialogResponse =
          await modalDialogService.showConfirmationDialog(SAVING_CLOSE_CONFIG);

        if (dialogResponse === CONFIRM_RESPONSE) {
          return router.createUrlTree([
            `/ws/${aptContext.engagementId()}/conflict`,
          ]);
        }

        if (dialogResponse === undefined) {
          // Check if the navigation is triggered by a browser refresh
          if (router.url === "/") {
            // Redirect to the engagement home URL
            return router.createUrlTree([`/ws/${aptContext.engagementId()}`]);
          }

          return false;
        }
      }
    } else {
      aptContext.shouldShowConflictWarning.set(true);
      if (router.url === "/") {
        // Redirect to the engagement home URL
        return router.createUrlTree([`/ws/${aptContext.engagementId()}`]);
      }
    }

    return true;
  }) satisfies CanActivateFn;
}

function getNodeIdFromPath(parent: ActivatedRouteSnapshot) {
  let possiblenodeId = parent.paramMap.get("documentId");
  if (possiblenodeId) {
    return possiblenodeId;
  }
  while (parent.parent) {
    parent = parent.parent;
    possiblenodeId = parent.paramMap.get("documentId");
    if (possiblenodeId) {
      return possiblenodeId;
    }
  }
  return undefined;
}
